import { zodResolver } from '@hookform/resolvers/zod';
import { useFormExtended } from '@plato/ui-legacy/lib/hooks/use-form-extended';
import { useEffect } from 'react';
import type { Control } from 'react-hook-form';
import z from 'zod';
import type { SalesDraftSalesConfigOutput } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import type { TransactionAddedItemEventSource } from '../../analytics/types/analytics-events.ts';
import { useAddProductToDraft } from './use-add-product-to-draft.ts';

export type ProductResult = {
    article_group_id?: string | null;
    article_group_name?: string | null;
    product_variant_id: string;
    product_variant_name: string;
};

export type UseAddProductFormOptions = {
    product: ProductResult;
    source: TransactionAddedItemEventSource;
};

export type UseAddProductFormHook = {
    control: Control<AddProductFormValues>;
    onSubmit: () => void;
    isLoading: boolean;
    isUpdating: boolean;
    salesConfig: SalesDraftSalesConfigOutput | undefined;
    salesUnit: string | undefined;
};

export type AddProductFormValues = z.infer<typeof addProductFormSchema>;

const addProductFormSchema = z.object({
    quantity: z.number(),
});

/**
 * Form-based add: fetches the product's sales config to seed an editable quantity field, then
 * delegates the actual add (analytics + mutation + draft refresh) to {@link useAddProductToDraft}.
 */
export function useAddProductForm({ product, source }: UseAddProductFormOptions): UseAddProductFormHook {
    const { addProduct, isAdding } = useAddProductToDraft();

    const { control, handleSubmit, reset } = useFormExtended({
        mode: 'all',
        defaultValues: {
            quantity: 1,
        },
        resolver: zodResolver(addProductFormSchema),
    });

    const { data: salesConfig, isLoading } =
        api.platoUser.query.salesDraft.getSalesDraft.getSalesConfig.useQuery({
            product_variant_id: product.product_variant_id,
        });

    const salesUnit = salesConfig?.quantity_unit_sales ?? salesConfig?.package_unit;

    useEffect(() => {
        if (salesConfig) {
            reset({
                quantity: salesConfig.sales_units_per_package_unit ?? 1,
            });
        }
    }, [reset, salesConfig]);

    const onSubmit = handleSubmit(({ quantity }: AddProductFormValues): void => {
        if (!salesUnit) {
            return;
        }

        addProduct(
            {
                product_variant_id: product.product_variant_id,
                article_group_id: product.article_group_id,
                quantity,
                quantity_unit: salesUnit,
            },
            source,
        );
    });

    return {
        control,
        isLoading,
        isUpdating: isAdding,
        onSubmit,
        salesConfig,
        salesUnit,
    };
}
